<script setup>
const props = defineProps({
  maxSizeMB: { type: Number, default: 2 },
});

const fileInput = ref(null);
const selectedFile = ref(null);
const previewUrl = ref(null);
const dragging = ref(false);

// expose callbacks
const emit = defineEmits(["onUpload", "onRemove", "onError"]);

const triggerFileInput = () => {
  fileInput.value.click();
};

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    processFile(file);
  }
};

const handleDragOver = (event) => {
  event.preventDefault();
  event.stopPropagation();
  dragging.value = true;
};

const handleDragLeave = (event) => {
  event.preventDefault();
  event.stopPropagation();
  dragging.value = false;
};

const handleDrop = (event) => {
  event.preventDefault();
  event.stopPropagation();
  dragging.value = false;

  const file = event.dataTransfer.files[0];
  if (file) {
    processFile(file);
  }
};

const processFile = (file) => {
  const maxSizeBytes = props.maxSizeMB * 1024 * 1024;

  if (file.size > maxSizeBytes) {
    emit("onError", `File size exceeds ${props.maxSizeMB}MB limit.`);
    return;
  }
  selectedFile.value = file;
  previewUrl.value = URL.createObjectURL(file);

  emit("onUpload", file);
};

const isImage = (file) => {
  return file && file.type.startsWith("image/");
};

const isPDF = (file) => {
  return file && file.type === "application/pdf";
};

const isDocx = (file) => {
  return (
    file &&
    (file.type === "application/msword" ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
  );
};

const removeFile = () => {
  emit("onRemove", selectedFile.value);
  selectedFile.value = null;
  previewUrl.value = null;
  fileInput.value.value = "";
};

onMounted(() => {
  window.addEventListener("dragover", preventDefault);
  window.addEventListener("drop", preventDefault);
});

onUnmounted(() => {
  window.removeEventListener("dragover", preventDefault);
  window.removeEventListener("drop", preventDefault);
});

const preventDefault = (event) => {
  event.preventDefault();
  event.stopPropagation();
};
</script>

<template>
  <div
    class="border-2 border-dashed border-primary-300 bg-gray-50 rounded-xl p-6 h-[200px] w-full flex flex-col items-center justify-center cursor-pointer relative hover:border-primary"
    @dragover.prevent="handleDragOver"
    @dragleave="handleDragLeave"
    @drop.prevent="handleDrop"
    @click="triggerFileInput"
  >
    <input
      type="file"
      class="hidden"
      ref="fileInput"
      @change="handleFileSelect"
      accept="image/*,.pdf,.doc,.docx"
    />

    <div v-if="!selectedFile">
      <p class="text-blue-500 font-semibold hover:text-blue-600">
        <i class="pi pi-upload mr-2"></i> Upload your receipt here
      </p>
      <p class="text-gray-500 text-sm mt-5">
        Drag and drop files here to upload.
      </p>
    </div>

    <div v-else class="relative">
      <template v-if="isImage(selectedFile)">
        <a
          :href="previewUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="z-20"
          @click.stop
        >
          <img
            :src="previewUrl"
            alt="File preview"
            class="max-w-xs rounded-lg shadow-md w-[90px] h-[90px] cursor-pointer"
          />
        </a>
      </template>

      <template v-else>
        <a
          :href="previewUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="z-20"
          @click.stop
        >
          <img
            src="~/assets/img/pdf.png"
            alt="PDF"
            class="w-[90px] h-[90px] cursor-pointer"
          />
        </a>
      </template>

      <button
        @click.stop="removeFile"
        class="absolute top-0 right-0 transform translate-x-2 -translate-y-2 bg-red-400 text-white text-xs rounded-full w-7 h-7 flex items-center justify-center shadow-md border-2 border-white transition duration-300 ease-in-out hover:bg-red-500 hover:scale-105"
      >
        <i class="pi pi-times text-lg"></i>
      </button>
    </div>

    <span class="text-sm mt-4 text-gray-500">{{ selectedFile?.name }}</span>

    <div
      v-if="dragging"
      class="absolute inset-0 bg-gray-100 bg-opacity-50 rounded-xl flex items-center justify-center pointer-events-none"
    >
      <p class="text-gray-700 font-semibold">Drop file here</p>
    </div>
  </div>
</template>
